import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  Autocomplete,
  TextField,
  CircularProgress,
  createFilterOptions,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, AppState } from "../store";
import {
  createAssignee,
  assignShipment,
  unassignShipment,
  assignTemporaryShipment,
  unassignTemporaryShipment,
} from "../store/shipmentSlice";
import type { ShipmentAssignee } from "@services/shipments/types";

interface AssigneeCellProps {
  shipmentId: string;
  assignedToIds: string[];
  isTemporary?: boolean;
}

export function AssigneeCell({
  shipmentId,
  assignedToIds,
  isTemporary = false,
}: AssigneeCellProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Get assignees from Redux store
  const { assigneeData, assigneeList, loading } = useSelector(
    (state: AppState) => state.shipment.assignees
  );

  // Convert assigneeList to array of assignees
  const allAssignees = useMemo(
    () => assigneeList.map((id) => assigneeData[id]).filter(Boolean),
    [assigneeList, assigneeData]
  );

  // Get assigned assignees for this shipment
  const assignedAssignees = useMemo(
    () =>
      assignedToIds
        .map((id) => assigneeData[id])
        .filter(Boolean) as ShipmentAssignee[],
    [assignedToIds, assigneeData]
  );

  // Helper to detect if a string is an email
  const isEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  // Handle assigning an assignee
  const handleAssign = async (assignee: ShipmentAssignee | string | null) => {
    if (!assignee) return;

    // Handle temporary shipments locally
    if (isTemporary) {
      // For temporary shipments, only handle existing assignees
      if (typeof assignee === "string") {
        const trimmedValue = assignee.trim();
        if (!trimmedValue) return;

        // For new assignees on temporary shipments, we need to create them first
        // Detect if it's an email or a name
        if (isEmail(trimmedValue)) {
          // Create assignee with userEmail
          const result = await dispatch(
            createAssignee({
              userEmail: trimmedValue,
              type: "email",
            })
          ).unwrap();

          // Add the created assignee to the temporary shipment
          if (result.id) {
            dispatch(
              assignTemporaryShipment({
                tempId: shipmentId,
                assigneeId: result.id,
              })
            );
          }
        } else {
          // Create assignee with name
          const result = await dispatch(
            createAssignee({
              name: trimmedValue,
              type: "name",
            })
          ).unwrap();

          // Add the created assignee to the temporary shipment
          if (result.id) {
            dispatch(
              assignTemporaryShipment({
                tempId: shipmentId,
                assigneeId: result.id,
              })
            );
          }
        }
      } else {
        // Handle existing assignee object
        const userNotAvailable = assignee.assigneeUserNotAvailable === true;

        if (userNotAvailable && assignee.userId) {
          // Auto-create assignee using userId
          const result = await dispatch(
            createAssignee({
              userId: assignee.userId,
              type: "dobby_user",
            })
          ).unwrap();

          if (result.id) {
            dispatch(
              assignTemporaryShipment({
                tempId: shipmentId,
                assigneeId: result.id,
              })
            );
          }
        } else if (!userNotAvailable && assignee.id) {
          // Use existing assignee - just add to temporary shipment locally
          dispatch(
            assignTemporaryShipment({
              tempId: shipmentId,
              assigneeId: assignee.id,
            })
          );
        }
      }
    } else {
      // Handle regular shipments with backend API
      // Check if assignee is a string (free-text input)
      if (typeof assignee === "string") {
        const trimmedValue = assignee.trim();
        if (!trimmedValue) return;

        // Detect if it's an email or a name
        if (isEmail(trimmedValue)) {
          // Create assignee with userEmail and assign to shipment
          await dispatch(
            assignShipment({
              shipmentId,
              userEmail: trimmedValue,
            })
          );
        } else {
          // Create assignee with name and assign to shipment
          await dispatch(
            assignShipment({
              shipmentId,
              name: trimmedValue,
            })
          );
        }
      } else {
        // Handle existing assignee object
        const userNotAvailable = assignee.assigneeUserNotAvailable === true;

        if (userNotAvailable && assignee.userId) {
          // Auto-create assignee using userId and assign to shipment
          await dispatch(
            assignShipment({
              shipmentId,
              userId: assignee.userId,
            })
          );
        } else if (!userNotAvailable && assignee.id) {
          // Use existing assignee
          await dispatch(
            assignShipment({
              shipmentId,
              assigneeId: assignee.id,
            })
          );
        }
      }
    }

    setIsAdding(false);
    setInputValue("");
  };

  // Handle unassigning an assignee
  const handleUnassign = async (assigneeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click

    if (isTemporary) {
      // For temporary shipments, update locally
      dispatch(
        unassignTemporaryShipment({
          tempId: shipmentId,
          assigneeId,
        })
      );
    } else {
      // For regular shipments, call backend
      await dispatch(
        unassignShipment({
          shipmentId,
          assigneeId,
        })
      );
    }
  };

  // Handle opening the autocomplete
  const handleOpenAutocomplete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setIsAdding(true);
  };

  // Handle closing the autocomplete
  const handleCloseAutocomplete = () => {
    setIsAdding(false);
    setInputValue("");
  };

  // Get display name for an assignee
  const getAssigneeName = (assignee: ShipmentAssignee): string => {
    return (
      assignee.user?.name ||
      assignee.name ||
      assignee.user?.email ||
      assignee.userEmail ||
      "Unknown"
    );
  };

  // Filter out already assigned assignees
  const availableAssignees = useMemo(
    () =>
      allAssignees.filter((assignee) => {
        // Keep virtual assignees (org users not yet added as assignees) - they have null IDs
        if (assignee.assigneeUserNotAvailable && !assignee.id) {
          return true;
        }
        // For real assignees, check if they're already assigned
        return assignee.id && !assignedToIds.includes(assignee.id);
      }),
    [allAssignees, assignedToIds]
  );

  // Create filter options to inject "Create new user" option
  const filterOptions = createFilterOptions<ShipmentAssignee | string>();

  const customFilterOptions = (
    options: (ShipmentAssignee | string)[],
    params: any
  ) => {
    const filtered = filterOptions(options as any, params) as (
      | ShipmentAssignee
      | string
    )[];

    const { inputValue } = params;

    // Suggest the creation of a new value
    const trimmedInput = inputValue.trim();
    if (trimmedInput !== "") {
      filtered.push(trimmedInput);
    }

    return filtered;
  };

  const isEmpty = assignedAssignees.length === 0;
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
        minHeight: "24px",
        width: "100%",
        padding: isEmpty ? "2px 4px" : 0,
        borderRadius: "4px",
        cursor: isEmpty && !isAdding ? "pointer" : "default",
        backgroundColor:
          isEmpty && isHovered && !isAdding ? "#f5f5f5" : "transparent",
        transition: "background-color 0.2s",
      }}
    >
      {/* Display assigned assignees as chips */}
      {assignedAssignees.map((assignee) => (
        <Chip
          key={assignee.id || `unknown-${assignee.userId}`}
          label={getAssigneeName(assignee)}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenAutocomplete(e);
          }}
          onDelete={
            assignee.id
              ? (e) => handleUnassign(assignee.id!, e as any)
              : undefined
          }
          deleteIcon={<CloseIcon fontSize="small" />}
          sx={{
            height: "20px",
            fontSize: "10px",
            cursor: "pointer",
            "& .MuiChip-label": {
              px: 1,
            },
            "& .MuiChip-deleteIcon": {
              fontSize: "14px",
            },
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          }}
        />
      ))}

      {/* Add button or autocomplete */}
      {isAdding ? (
        <Autocomplete
          open
          freeSolo
          onClose={handleCloseAutocomplete}
          value={null}
          onChange={(_event, newValue) => {
            handleAssign(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          options={availableAssignees}
          filterOptions={customFilterOptions}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return getAssigneeName(option);
          }}
          loading={loading}
          size="small"
          sx={{
            width: 200,
            "& .MuiAutocomplete-inputRoot": {
              fontSize: "10px",
              padding: "2px 4px",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Search users..."
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const displayText =
              typeof option === "string"
                ? `Create new user: "${option}"`
                : getAssigneeName(option);

            return (
              <li {...props} style={{ fontSize: "10px", padding: "4px 8px" }}>
                {displayText}
              </li>
            );
          }}
        />
      ) : isEmpty ? (
        // Empty state with full-width clickable area
        <Box
          onClick={handleOpenAutocomplete}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            cursor: "pointer",
          }}
        >
          <IconButton
            size="small"
            sx={{
              width: 20,
              height: 20,
              padding: 0,
              backgroundColor: isHovered ? "#1976d2" : "transparent",
              color: isHovered ? "#ffffff" : "inherit",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#1976d2",
                color: "#ffffff",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
          {isHovered && (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              assign
            </Box>
          )}
        </Box>
      ) : (
        // Non-empty state with just the add button
        <IconButton
          size="small"
          onClick={handleOpenAutocomplete}
          sx={{
            width: 20,
            height: 20,
            padding: 0,
            "&:hover": {
              backgroundColor: "#f0f0f0",
            },
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}
